// AmbientVFX.js - PixiJS rendering for ambient visual effects

import { Container, Graphics } from 'pixi.js-legacy';

const FADE_DURATION_MS = 1000;
// Use setInterval at ~20fps for VFX animations to avoid scroll interference
// requestAnimationFrame at 60fps was causing scroll "fighting"
const VFX_UPDATE_INTERVAL_MS = 50; // 20fps - smooth enough for ambient effects

/**
 * Base class for ambient effects
 */
class AmbientEffect {
  constructor(app, gridWidth, gridHeight, TILE_SIZE) {
    this.app = app;
    this.gridWidth = gridWidth;
    this.gridHeight = gridHeight;
    this.TILE_SIZE = TILE_SIZE;
    this.container = new Container();
    this.container.alpha = 0;
    // Disable interactivity on VFX container to prevent hit testing
    this.container.interactiveChildren = false;
    this.container.eventMode = 'none';
    this.isActive = false;
    this.updateInterval = null;
    this.fadeAnimationFrame = null;
  }

  fadeIn() {
    const startTime = Date.now();
    const startAlpha = this.container.alpha;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / FADE_DURATION_MS, 1);
      this.container.alpha = startAlpha + (1 - startAlpha) * progress;

      if (progress < 1) {
        this.fadeAnimationFrame = requestAnimationFrame(animate);
      }
    };

    if (this.fadeAnimationFrame) {
      cancelAnimationFrame(this.fadeAnimationFrame);
    }
    animate();
  }

  fadeOut(onComplete) {
    const startTime = Date.now();
    const startAlpha = this.container.alpha;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / FADE_DURATION_MS, 1);
      this.container.alpha = startAlpha * (1 - progress);

      if (progress < 1) {
        this.fadeAnimationFrame = requestAnimationFrame(animate);
      } else {
        this.stop();
        if (onComplete) onComplete();
      }
    };

    if (this.fadeAnimationFrame) {
      cancelAnimationFrame(this.fadeAnimationFrame);
    }
    animate();
  }

  start() {
    this.isActive = true;
  }

  stop() {
    this.isActive = false;
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  destroy() {
    this.stop();
    if (this.fadeAnimationFrame) {
      cancelAnimationFrame(this.fadeAnimationFrame);
      this.fadeAnimationFrame = null;
    }
    if (this.container.parent) {
      this.container.parent.removeChild(this.container);
    }
    this.container.destroy({ children: true });
  }
}

/**
 * Butterflies effect - organic floating particles in packs
 */
class ButterfliesEffect extends AmbientEffect {
  constructor(app, gridWidth, gridHeight, TILE_SIZE) {
    super(app, gridWidth, gridHeight, TILE_SIZE);
    this.packs = [];
    this.packCount = 16; // Number of packs (doubled for more butterflies)
    this.colors = [0xFFB347, 0xFF6B6B, 0x87CEEB, 0xDDA0DD, 0x98FB98, 0xFFD700];
    this.init();
  }

  init() {
    const worldWidth = this.gridWidth * this.TILE_SIZE;
    const worldHeight = this.gridHeight * this.TILE_SIZE;

    for (let p = 0; p < this.packCount; p++) {
      // Each pack has 2-5 butterflies
      const packSize = 2 + Math.floor(Math.random() * 4);

      // Pack center position
      const packCenterX = Math.random() * worldWidth;
      const packCenterY = Math.random() * worldHeight;

      // Shared pack movement direction (with individual variation)
      const packAngle = Math.random() * Math.PI * 2;
      const packSpeed = 0.3 + Math.random() * 0.3;
      const packVx = Math.cos(packAngle) * packSpeed;
      const packVy = Math.sin(packAngle) * packSpeed * 0.6;

      const pack = {
        vx: packVx,
        vy: packVy,
        turnPhase: Math.random() * Math.PI * 2,
        butterflies: []
      };

      for (let i = 0; i < packSize; i++) {
        const butterfly = this.createButterfly();

        // Position near pack center with some spread
        butterfly.x = packCenterX + (Math.random() - 0.5) * 60;
        butterfly.y = packCenterY + (Math.random() - 0.5) * 60;

        // Individual movement variation relative to pack
        butterfly.individualVx = (Math.random() - 0.5) * 0.15;
        butterfly.individualVy = (Math.random() - 0.5) * 0.1;

        // Movement properties for fluttering
        butterfly.phase = Math.random() * Math.PI * 2;
        butterfly.speed = 0.3 + Math.random() * 0.4;
        butterfly.amplitude = 15 + Math.random() * 25;
        butterfly.wingPhase = Math.random() * Math.PI * 2;
        butterfly.wingSpeed = 0.1 + Math.random() * 0.05;

        pack.butterflies.push(butterfly);
        this.container.addChild(butterfly);
      }

      this.packs.push(pack);
    }
  }

  createButterfly() {
    const container = new Container();
    const color = this.colors[Math.floor(Math.random() * this.colors.length)];
    const size = 4 + Math.random() * 4;

    // Left wing
    const leftWing = new Graphics();
    leftWing.beginFill(color, 0.8);
    leftWing.drawEllipse(-size * 0.6, 0, size, size * 0.6);
    leftWing.endFill();
    leftWing.name = 'leftWing';
    container.addChild(leftWing);

    // Right wing
    const rightWing = new Graphics();
    rightWing.beginFill(color, 0.8);
    rightWing.drawEllipse(size * 0.6, 0, size, size * 0.6);
    rightWing.endFill();
    rightWing.name = 'rightWing';
    container.addChild(rightWing);

    // Body
    const body = new Graphics();
    body.beginFill(0x333333, 0.9);
    body.drawEllipse(0, 0, size * 0.15, size * 0.5);
    body.endFill();
    container.addChild(body);

    return container;
  }

  start() {
    super.start();
    this.lastTime = Date.now();
    // Use setInterval at lower frequency to avoid scroll interference
    this.updateInterval = setInterval(() => this.animate(), VFX_UPDATE_INTERVAL_MS);
  }

  animate() {
    if (!this.isActive) return;

    const now = Date.now();
    const delta = (now - this.lastTime) / 16.67; // Normalize to ~60fps equivalent
    this.lastTime = now;

    const worldWidth = this.gridWidth * this.TILE_SIZE;
    const worldHeight = this.gridHeight * this.TILE_SIZE;

    for (const pack of this.packs) {
      // Update pack turn phase for gentle drifting direction changes
      pack.turnPhase += 0.008 * delta;

      // Apply gentle turn to pack velocity
      const turnAmount = Math.sin(pack.turnPhase) * 0.003 * delta;
      const currentAngle = Math.atan2(pack.vy, pack.vx);
      const newAngle = currentAngle + turnAmount;
      const speed = Math.sqrt(pack.vx * pack.vx + pack.vy * pack.vy);
      pack.vx = Math.cos(newAngle) * speed;
      pack.vy = Math.sin(newAngle) * speed;

      // Occasional larger direction change for the whole pack
      if (Math.random() < 0.003) { // Adjusted for lower update rate
        const newPackAngle = Math.random() * Math.PI * 2;
        const newPackSpeed = 0.3 + Math.random() * 0.3;
        pack.vx = Math.cos(newPackAngle) * newPackSpeed;
        pack.vy = Math.sin(newPackAngle) * newPackSpeed * 0.6;
      }

      // Calculate pack center for wrapping
      let packCenterX = 0;
      let packCenterY = 0;
      for (const butterfly of pack.butterflies) {
        packCenterX += butterfly.x;
        packCenterY += butterfly.y;
      }
      packCenterX /= pack.butterflies.length;
      packCenterY /= pack.butterflies.length;

      // Check if pack needs to wrap
      const margin = 100;
      let wrapX = 0;
      let wrapY = 0;
      if (packCenterX < -margin) wrapX = worldWidth + margin * 2;
      if (packCenterX > worldWidth + margin) wrapX = -(worldWidth + margin * 2);
      if (packCenterY < -margin) wrapY = worldHeight + margin * 2;
      if (packCenterY > worldHeight + margin) wrapY = -(worldHeight + margin * 2);

      for (const butterfly of pack.butterflies) {
        // Update phase for sine wave movement
        butterfly.phase += butterfly.speed * 0.02 * delta;
        butterfly.wingPhase += butterfly.wingSpeed * delta;

        // Calculate actual movement for this frame (pack velocity + individual variation)
        const sineOffset = Math.sin(butterfly.phase) * butterfly.amplitude * 0.01;
        const actualVx = (pack.vx + butterfly.individualVx + sineOffset) * delta;
        const actualVy = (pack.vy + butterfly.individualVy) * delta + Math.cos(butterfly.phase * 0.5) * 0.3 * delta;

        // Apply movement
        butterfly.x += actualVx;
        butterfly.y += actualVy;

        // Apply wrap if needed
        if (wrapX !== 0) butterfly.x += wrapX;
        if (wrapY !== 0) butterfly.y += wrapY;

        // Wing flapping animation
        const wingAngle = Math.sin(butterfly.wingPhase) * 0.4;
        const leftWing = butterfly.getChildByName('leftWing');
        const rightWing = butterfly.getChildByName('rightWing');
        if (leftWing) leftWing.scale.x = 0.6 + Math.abs(wingAngle) * 0.4;
        if (rightWing) rightWing.scale.x = 0.6 + Math.abs(wingAngle) * 0.4;

        // Rotate butterfly to point in movement direction
        // atan2 gives angle from positive x-axis, add PI/2 to orient body forward
        const targetRotation = Math.atan2(actualVy, actualVx) + Math.PI / 2;
        // Smooth rotation interpolation to avoid jarring snaps
        const rotationSpeed = 0.1;
        let rotationDiff = targetRotation - butterfly.rotation;
        // Normalize to -PI to PI range
        while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
        while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
        butterfly.rotation += rotationDiff * rotationSpeed;
      }
    }
  }
}

/**
 * Base class for cloud-type effects (clouds, darkClouds, lavaClouds, poisonClouds)
 * Provides shared initialization, animation, and wrapping logic.
 * Subclasses customize via getCloudConfig() method.
 */
class BaseCloudEffect extends AmbientEffect {
  constructor(app, gridWidth, gridHeight, TILE_SIZE) {
    super(app, gridWidth, gridHeight, TILE_SIZE);
    this.clouds = [];
    this.worldWidth = gridWidth * TILE_SIZE;
    this.worldHeight = gridHeight * TILE_SIZE;
    this.init();
  }

  /**
   * Override in subclass to provide cloud configuration.
   * @returns {{
   *   densityMultiplier: number,    // Multiplier on base cloud count (1.0 = baseline)
   *   speedMin: number,             // Minimum drift speed
   *   speedMax: number,             // Maximum drift speed (added to min)
   *   alphaMin: number,             // Minimum target alpha
   *   alphaMax: number,             // Maximum additional alpha
   *   baseColors: number[],         // Array of base colors for puffs
   *   highlightColors: number[],    // Array of highlight colors for small puffs
   *   puffAlphaMin: number,         // Minimum puff alpha
   *   puffAlphaVariation: number    // Additional random alpha for puffs
   * }}
   */
  getCloudConfig() {
    // Default config - white clouds
    return {
      densityMultiplier: 1.0,
      speedMin: 0.25,
      speedMax: 0.2,
      alphaMin: 0.25,
      alphaMax: 0.15,
      baseColors: [0xFFFFFF],
      highlightColors: [0xFFFFFF],
      puffAlphaMin: 0.5,
      puffAlphaVariation: 0.2
    };
  }

  init() {
    const config = this.getCloudConfig();

    // Base cloud count of 16, modified by density multiplier
    const baseCloudCount = 16;
    const cloudCount = Math.round(baseCloudCount * config.densityMultiplier);

    // Divide grid into 4 vertical sections for guaranteed coverage
    // Each section gets an equal share of clouds
    const sectionsCount = 4;
    const sectionHeight = this.worldHeight / sectionsCount;
    const cloudsPerSection = Math.floor(cloudCount / sectionsCount);
    const extraClouds = cloudCount % sectionsCount;

    let cloudIndex = 0;

    for (let section = 0; section < sectionsCount; section++) {
      // Calculate how many clouds go in this section
      // Distribute extra clouds to earlier sections
      const cloudsInThisSection = cloudsPerSection + (section < extraClouds ? 1 : 0);
      const sectionTop = section * sectionHeight;

      for (let i = 0; i < cloudsInThisSection; i++) {
        const cloud = this.createCloud(config);

        // Place cloud randomly within this section's vertical band
        cloud.y = sectionTop + (Math.random() * sectionHeight);

        // Random X position across full width
        cloud.x = Math.random() * this.worldWidth;

        // Randomly move left or right
        const movesRight = Math.random() > 0.5;
        cloud.vx = (movesRight ? 1 : -1) * (config.speedMin + Math.random() * config.speedMax);

        // Semi-transparent (store target alpha for fading)
        cloud.targetAlpha = config.alphaMin + Math.random() * config.alphaMax;
        cloud.alpha = cloud.targetAlpha;

        // Store section info for repositioning on wrap
        cloud.worldHeight = this.worldHeight;
        cloud.sectionHeight = sectionHeight;
        cloud.sectionsCount = sectionsCount;

        this.clouds.push(cloud);
        this.container.addChild(cloud);
        cloudIndex++;
      }
    }
  }

  createCloud(config) {
    const container = new Container();
    // Reduced from 6-10 puffs to 3-5 for better performance
    const numPuffs = 3 + Math.floor(Math.random() * 3);
    const baseSize = 50 + Math.random() * 70;

    // Create overlapping circles for billowy effect
    for (let i = 0; i < numPuffs; i++) {
      const puff = new Graphics();
      const color = config.baseColors[Math.floor(Math.random() * config.baseColors.length)];
      puff.beginFill(color, config.puffAlphaMin + Math.random() * config.puffAlphaVariation);
      const puffSize = baseSize * (0.5 + Math.random() * 0.5);
      puff.drawCircle(
        (i - numPuffs / 2) * baseSize * 0.4 + (Math.random() - 0.5) * baseSize * 0.3,
        (Math.random() - 0.5) * baseSize * 0.5,
        puffSize
      );
      puff.endFill();
      container.addChild(puff);
    }

    // Add 1 smaller puff on top for texture (reduced from 3)
    const smallPuff = new Graphics();
    const highlightColor = config.highlightColors[Math.floor(Math.random() * config.highlightColors.length)];
    smallPuff.beginFill(highlightColor, 0.4);
    const smallSize = baseSize * (0.3 + Math.random() * 0.3);
    smallPuff.drawCircle(
      (Math.random() - 0.5) * baseSize * 1.5,
      (Math.random() - 0.5) * baseSize * 0.4,
      smallSize
    );
    smallPuff.endFill();
    container.addChild(smallPuff);

    return container;
  }

  start() {
    super.start();
    this.lastTime = Date.now();
    // Use setInterval at lower frequency to avoid scroll interference
    this.updateInterval = setInterval(() => this.animate(), VFX_UPDATE_INTERVAL_MS);
  }

  animate() {
    if (!this.isActive) return;

    const now = Date.now();
    const delta = (now - this.lastTime) / 16.67;
    this.lastTime = now;

    const margin = 150;
    const fadeZone = 100;
    const fadeSpeed = 0.03;

    for (const cloud of this.clouds) {
      cloud.x += cloud.vx * delta;

      // Wrap around based on direction
      // On wrap, assign to a random section for even distribution over time
      if (cloud.vx > 0 && cloud.x > this.worldWidth + margin) {
        cloud.x = -margin;
        const randomSection = Math.floor(Math.random() * cloud.sectionsCount);
        cloud.y = (randomSection * cloud.sectionHeight) + (Math.random() * cloud.sectionHeight);
        cloud.alpha = 0;
      } else if (cloud.vx < 0 && cloud.x < -margin) {
        cloud.x = this.worldWidth + margin;
        const randomSection = Math.floor(Math.random() * cloud.sectionsCount);
        cloud.y = (randomSection * cloud.sectionHeight) + (Math.random() * cloud.sectionHeight);
        cloud.alpha = 0;
      }

      // Calculate target alpha based on position (fade near edges)
      let edgeFade = 1;
      if (cloud.vx > 0) {
        if (cloud.x < fadeZone) {
          edgeFade = Math.max(0, cloud.x / fadeZone);
        } else if (cloud.x > this.worldWidth - fadeZone) {
          edgeFade = Math.max(0, (this.worldWidth - cloud.x) / fadeZone);
        }
      } else {
        if (cloud.x > this.worldWidth - fadeZone) {
          edgeFade = Math.max(0, (this.worldWidth - cloud.x) / fadeZone + 1);
        } else if (cloud.x < fadeZone) {
          edgeFade = Math.max(0, cloud.x / fadeZone);
        }
      }

      const targetAlpha = cloud.targetAlpha * edgeFade;
      cloud.alpha += (targetAlpha - cloud.alpha) * fadeSpeed * delta;
    }
  }
}

/**
 * Clouds effect - billowy white clouds drifting across the grid
 */
class CloudsEffect extends BaseCloudEffect {
  getCloudConfig() {
    return {
      densityMultiplier: 1.0,  // Baseline: 16 clouds
      speedMin: 0.25,
      speedMax: 0.2,
      alphaMin: 0.25,
      alphaMax: 0.15,
      baseColors: [0xFFFFFF],
      highlightColors: [0xFFFFFF],
      puffAlphaMin: 0.5,
      puffAlphaVariation: 0.2
    };
  }
}

/**
 * Birds effect - large ravens flying across the grid in flocks
 */
class BirdsEffect extends AmbientEffect {
  constructor(app, gridWidth, gridHeight, TILE_SIZE) {
    super(app, gridWidth, gridHeight, TILE_SIZE);
    this.flocks = [];
    this.flockCount = 6; // More flocks for more frequent birds
    this.init();
  }

  init() {
    const worldWidth = this.gridWidth * this.TILE_SIZE;
    const worldHeight = this.gridHeight * this.TILE_SIZE;

    for (let f = 0; f < this.flockCount; f++) {
      // 50% single birds, 50% flocks of 3
      const isSingleBird = Math.random() < 0.5;
      const birdsInThisFlock = isSingleBird ? 1 : 3;

      // Each flock has a leader position and shared direction
      const flockLeaderX = Math.random() * worldWidth;
      const flockLeaderY = Math.random() * worldHeight * 0.6;
      const flockAngle = Math.random() * Math.PI * 2;
      const flockSpeed = 1.0 + Math.random() * 0.4; // Faster flight

      // Flock turning parameters (shared by all birds in flock)
      const flockTurnRate = (Math.random() - 0.5) * 0.004; // Gentler curves for longer arcs
      const flockTurnPhase = Math.random() * Math.PI * 2;

      const flock = {
        vx: Math.cos(flockAngle) * flockSpeed,
        vy: Math.sin(flockAngle) * flockSpeed * 0.4,
        turnRate: flockTurnRate,
        turnPhase: flockTurnPhase,
        birds: []
      };

      // Create birds in a loose V formation (or just one for single birds)
      for (let i = 0; i < birdsInThisFlock; i++) {
        const bird = this.createBird();

        // Position in V formation relative to leader
        // Bird 0 is leader, birds 1 and 2 are behind and to the sides
        let offsetX = 0;
        let offsetY = 0;
        if (i === 1) {
          offsetX = -25 - Math.random() * 10;
          offsetY = -20 - Math.random() * 10;
        } else if (i === 2) {
          offsetX = -25 - Math.random() * 10;
          offsetY = 20 + Math.random() * 10;
        }

        bird.x = flockLeaderX + offsetX;
        bird.y = flockLeaderY + offsetY;

        // Store formation offset for maintaining flock cohesion
        bird.formationOffsetX = offsetX;
        bird.formationOffsetY = offsetY;

        // Small individual variation in movement
        bird.individualDrift = {
          x: (Math.random() - 0.5) * 0.02,
          y: (Math.random() - 0.5) * 0.02
        };

        // Wing flapping (slightly different phase per bird)
        bird.wingPhase = Math.random() * Math.PI * 2;
        bird.wingSpeed = 0.07 + Math.random() * 0.02;

        // Current rotation
        bird.rotation = flockAngle;

        flock.birds.push(bird);
        this.container.addChild(bird);
      }

      this.flocks.push(flock);
    }
  }

  createBird() {
    const container = new Container();
    const size = 12 + Math.random() * 6; // Bigger than butterflies

    // Bird is drawn facing RIGHT (positive x direction)
    // When rotation = 0, bird faces right

    // Tail feathers (behind body, to the left)
    const tail = new Graphics();
    tail.beginFill(0x1a1a1a, 0.9);
    tail.moveTo(-size * 0.6, 0);
    tail.lineTo(-size * 1.2, -size * 0.2);
    tail.lineTo(-size * 1.3, 0);
    tail.lineTo(-size * 1.2, size * 0.2);
    tail.lineTo(-size * 0.6, 0);
    tail.endFill();
    container.addChild(tail);

    // Body - elongated dark shape (horizontal, along x-axis)
    const body = new Graphics();
    body.beginFill(0x1a1a1a, 0.95);
    body.drawEllipse(0, 0, size * 0.6, size * 0.2);
    body.endFill();
    container.addChild(body);

    // Left wing - extends UP from body (negative y)
    const leftWing = new Graphics();
    leftWing.beginFill(0x2a2a2a, 0.9);
    leftWing.moveTo(-size * 0.2, 0);
    leftWing.lineTo(-size * 0.1, -size * 1.2);
    leftWing.lineTo(size * 0.3, -size * 0.8);
    leftWing.lineTo(size * 0.2, 0);
    leftWing.endFill();
    leftWing.name = 'leftWing';
    container.addChild(leftWing);

    // Right wing - extends DOWN from body (positive y)
    const rightWing = new Graphics();
    rightWing.beginFill(0x2a2a2a, 0.9);
    rightWing.moveTo(-size * 0.2, 0);
    rightWing.lineTo(-size * 0.1, size * 1.2);
    rightWing.lineTo(size * 0.3, size * 0.8);
    rightWing.lineTo(size * 0.2, 0);
    rightWing.endFill();
    rightWing.name = 'rightWing';
    container.addChild(rightWing);

    // Head (to the right of body)
    const head = new Graphics();
    head.beginFill(0x1a1a1a, 0.95);
    head.drawCircle(size * 0.7, 0, size * 0.22);
    head.endFill();
    container.addChild(head);

    // Beak (pointing right from head)
    const beak = new Graphics();
    beak.beginFill(0x4a4a4a, 0.95);
    beak.moveTo(size * 0.85, -size * 0.05);
    beak.lineTo(size * 1.15, 0);
    beak.lineTo(size * 0.85, size * 0.05);
    beak.lineTo(size * 0.85, -size * 0.05);
    beak.endFill();
    container.addChild(beak);

    return container;
  }

  start() {
    super.start();
    this.lastTime = Date.now();
    // Use setInterval at lower frequency to avoid scroll interference
    this.updateInterval = setInterval(() => this.animate(), VFX_UPDATE_INTERVAL_MS);
  }

  animate() {
    if (!this.isActive) return;

    const now = Date.now();
    const delta = (now - this.lastTime) / 16.67; // Normalize to ~60fps equivalent
    this.lastTime = now;

    const worldWidth = this.gridWidth * this.TILE_SIZE;
    const worldHeight = this.gridHeight * this.TILE_SIZE;

    for (const flock of this.flocks) {
      // Update flock turn phase for gradual curving (slower phase = longer arcs)
      flock.turnPhase += 0.003 * delta;

      // Apply gentle turn to flock velocity direction
      const turnAmount = Math.sin(flock.turnPhase) * flock.turnRate * delta;
      const currentAngle = Math.atan2(flock.vy, flock.vx);
      const newAngle = currentAngle + turnAmount;
      const speed = Math.sqrt(flock.vx * flock.vx + flock.vy * flock.vy);
      flock.vx = Math.cos(newAngle) * speed;
      flock.vy = Math.sin(newAngle) * speed;

      // Get the leader bird (first in flock)
      const leader = flock.birds[0];

      // Move leader
      leader.x += flock.vx * delta;
      leader.y += flock.vy * delta;

      // Wrap leader around with large margin
      const margin = 200;
      let wrapped = false;
      if (leader.x < -margin) { leader.x = worldWidth + margin; wrapped = true; }
      if (leader.x > worldWidth + margin) { leader.x = -margin; wrapped = true; }
      if (leader.y < -margin) { leader.y = worldHeight + margin; wrapped = true; }
      if (leader.y > worldHeight + margin) { leader.y = -margin; wrapped = true; }

      // Move follower birds to maintain formation
      for (let i = 0; i < flock.birds.length; i++) {
        const bird = flock.birds[i];

        if (i > 0) {
          // Calculate target position based on formation offset rotated by flock direction
          const flockAngle = Math.atan2(flock.vy, flock.vx);
          const cos = Math.cos(flockAngle);
          const sin = Math.sin(flockAngle);

          // Rotate formation offset by flock direction
          const rotatedOffsetX = bird.formationOffsetX * cos - bird.formationOffsetY * sin;
          const rotatedOffsetY = bird.formationOffsetX * sin + bird.formationOffsetY * cos;

          const targetX = leader.x + rotatedOffsetX;
          const targetY = leader.y + rotatedOffsetY;

          // If leader wrapped, snap followers to new position
          if (wrapped) {
            bird.x = targetX;
            bird.y = targetY;
          } else {
            // Smoothly follow formation position with slight drift
            bird.x += (targetX - bird.x) * 0.05 * delta + bird.individualDrift.x * delta;
            bird.y += (targetY - bird.y) * 0.05 * delta + bird.individualDrift.y * delta;
          }
        }

        // Wing flapping - slightly different timing per bird
        bird.wingPhase += bird.wingSpeed * delta;
        const wingFlap = 0.6 + Math.abs(Math.sin(bird.wingPhase)) * 0.4;
        const leftWing = bird.getChildByName('leftWing');
        const rightWing = bird.getChildByName('rightWing');
        if (leftWing) leftWing.scale.y = wingFlap;
        if (rightWing) rightWing.scale.y = wingFlap;

        // Rotate bird to face flock movement direction (smooth)
        const targetRotation = Math.atan2(flock.vy, flock.vx);
        const rotationSpeed = 0.05;
        let rotationDiff = targetRotation - bird.rotation;
        // Normalize to -PI to PI
        while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
        while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
        bird.rotation += rotationDiff * rotationSpeed;
      }
    }
  }
}

/**
 * Dark clouds effect - ominous gray clouds drifting across the grid
 */
class DarkCloudsEffect extends BaseCloudEffect {
  getCloudConfig() {
    // Generate array of gray colors for variation
    const grayColors = [];
    const grayHighlights = [];
    for (let i = 0; i < 5; i++) {
      const grayValue = 0x45 + Math.floor(Math.random() * 0x30); // 0x45-0x75 range
      grayColors.push((grayValue << 16) | (grayValue << 8) | grayValue);
      const highlightValue = Math.min(0x85, grayValue + 0x10);
      grayHighlights.push((highlightValue << 16) | (highlightValue << 8) | highlightValue);
    }

    return {
      densityMultiplier: 3.0,  // 3x density: 48 clouds for heavy overcast sky
      speedMin: 0.25,
      speedMax: 0.2,
      alphaMin: 0.35,
      alphaMax: 0.25,
      baseColors: grayColors,
      highlightColors: grayHighlights,
      puffAlphaMin: 0.55,
      puffAlphaVariation: 0.2
    };
  }
}

/**
 * Lava clouds effect - red/orange volcanic clouds
 */
class LavaCloudsEffect extends BaseCloudEffect {
  getCloudConfig() {
    return {
      densityMultiplier: 1.5,  // 1.5x density: 24 clouds
      speedMin: 0.20,          // Slightly slower, more ominous
      speedMax: 0.15,
      alphaMin: 0.35,
      alphaMax: 0.20,
      baseColors: [
        0x8B0000,   // Dark red
        0xA52A2A,   // Brown-red
        0xB22222,   // Firebrick
        0xCD5C5C,   // Indian red
        0xFF4500    // Orange-red
      ],
      highlightColors: [
        0xFF6347,   // Tomato (brighter)
        0xFF7F50,   // Coral
        0xFFA500    // Orange highlight
      ],
      puffAlphaMin: 0.5,
      puffAlphaVariation: 0.25
    };
  }
}

/**
 * Poison clouds effect - green toxic clouds
 */
class PoisonCloudsEffect extends BaseCloudEffect {
  getCloudConfig() {
    return {
      densityMultiplier: 1.25,  // 1.25x density: 20 clouds
      speedMin: 0.18,           // Slow, lingering poison
      speedMax: 0.12,
      alphaMin: 0.30,
      alphaMax: 0.20,
      baseColors: [
        0x228B22,   // Forest green
        0x2E8B57,   // Sea green
        0x006400,   // Dark green
        0x3CB371,   // Medium sea green
        0x32CD32    // Lime green
      ],
      highlightColors: [
        0x7CFC00,   // Lawn green (bright)
        0x90EE90,   // Light green
        0x98FB98    // Pale green
      ],
      puffAlphaMin: 0.45,
      puffAlphaVariation: 0.25
    };
  }
}

// Export effect factory
export const createAmbientEffect = (effectName, app, gridWidth, gridHeight, TILE_SIZE) => {
  switch (effectName) {
    case 'butterflies':
      return new ButterfliesEffect(app, gridWidth, gridHeight, TILE_SIZE);
    case 'birds':
      return new BirdsEffect(app, gridWidth, gridHeight, TILE_SIZE);
    case 'clouds':
      return new CloudsEffect(app, gridWidth, gridHeight, TILE_SIZE);
    case 'darkClouds':
      return new DarkCloudsEffect(app, gridWidth, gridHeight, TILE_SIZE);
    case 'lavaClouds':
      return new LavaCloudsEffect(app, gridWidth, gridHeight, TILE_SIZE);
    case 'poisonClouds':
      return new PoisonCloudsEffect(app, gridWidth, gridHeight, TILE_SIZE);
    default:
      console.warn(`Unknown ambient effect: ${effectName}`);
      return null;
  }
};

export {
  ButterfliesEffect,
  BirdsEffect,
  CloudsEffect,
  DarkCloudsEffect,
  LavaCloudsEffect,
  PoisonCloudsEffect,
  BaseCloudEffect
};
